/**
 * Apple Sign-In utility functions
 * Uses Apple's Sign in with Apple JavaScript SDK
 */

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          usePopup?: boolean;
          responseMode?: "web_message" | "form_post" | "fragment";
          responseType?: string;
          state?: string;
          nonce?: string;
        }) => void;
        signIn: (config?: { requestedScopes?: string[] }) => Promise<{
          authorization: {
            code: string;
            id_token: string;
            state: string;
          };
          user: {
            email?: string;
            name?: {
              firstName: string;
              lastName: string;
            };
          };
        }>;
      };
    };
  }
}

const getAppleRedirectUri = (override?: string): string => {
  if (override) return override;
  const fromEnv = import.meta.env.VITE_APPLE_REDIRECT_URI as string | undefined;
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined") {
    return `${window.location.origin}/login`;
  }
  return "";
};

/**
 * Load Apple Sign-In script
 */
export const loadAppleScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.AppleID?.auth) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="appleid.authkit"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () =>
        reject(new Error("Failed to load Apple script"))
      );
      return;
    }

    // Load Apple Sign-In script
    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Apple script"));
    document.head.appendChild(script);
  });
};

/**
 * Initialize Apple Sign-In
 * @param clientId - Apple Service ID
 * @param redirectURI - Redirect URI (uses current page URL if not provided)
 * @param usePopup - Whether to use popup mode (default: true)
 */
export const initializeAppleSignIn = (
  clientId: string,
  redirectURI?: string,
  usePopup: boolean = true
): void => {
  if (!window.AppleID?.auth) {
    throw new Error("Apple Sign-In not loaded. Call loadAppleScript() first.");
  }

  const redirect = getAppleRedirectUri(redirectURI);
  window.AppleID.auth.init({
    clientId,
    scope: "name email",
    redirectURI: redirect,
    usePopup,
    responseMode: "web_message",
    responseType: "code id_token",
    state: "apple_signin_state",
    nonce: "apple_signin_nonce",
  });
};

/**
 * Trigger Apple Sign-In
 * @param clientId - Apple Service ID
 * @param callback - Callback function that receives the identityToken and user data
 * @param onError - Optional error callback
 */
export const triggerAppleSignIn = (
  clientId: string,
  callback: (
    identityToken: string,
    user: { name: { firstName: string; lastName: string } } | null
  ) => void,
  onError?: (error: Error) => void
): void => {
  if (!window.AppleID?.auth) {
    const error = new Error("Apple Sign-In not loaded. Call loadAppleScript() first.");
    onError?.(error);
    throw error;
  }

  try {
    // Initialize if not already initialized (use current page as redirect URI)
    initializeAppleSignIn(clientId, getAppleRedirectUri(), true);

    // Trigger sign-in
    window.AppleID.auth
      .signIn({
        requestedScopes: ["name", "email"],
      })
      .then((response) => {
        const identityToken = response.authorization.id_token;
        const user = response.user
          ? {
              name: {
                firstName: response.user.name?.firstName || "",
                lastName: response.user.name?.lastName || "",
              },
            }
          : null;
        callback(identityToken, user);
      })
      .catch((error) => {
        const err = error instanceof Error ? error : new Error("Failed to sign in with Apple");
        onError?.(err);
      });
  } catch (error) {
    const err = error instanceof Error ? error : new Error("Failed to trigger Apple Sign-In");
    onError?.(err);
    throw err;
  }
};
